import React from "react";
import CollapseCard from "../../../Home/components/CollapseCard";
import GroupHeader from "../../../Home/components/ColumnComponents/GroupHeader";
import HiddenGroups from "../../../Home/components/ColumnComponents/HiddenGroups";
import SubGroupHeader from "../../../Home/components/ColumnComponents/SubGroupHeader";
import { SubgroupCell } from "../../../Home/components/ColumnComponents/SubgroupCell";
import { BoardViewType } from "app/types";
import { TicketGroup } from "../../types";

interface Props {
  visibleGroups: TicketGroup[];
  subGroups: TicketGroup[];
  subGroupStatsMap: Record<
    string,
    { count: number; limit: number | undefined }
  >;
  isSubgroupCollapsed: Record<string, boolean>;
  groupLoading: boolean;
  getSubgroupKey: (id: string | string[]) => string;
}

export function KanbanSubgroupLayout({
  visibleGroups,
  subGroups,
  subGroupStatsMap,
  isSubgroupCollapsed,
  groupLoading,
  getSubgroupKey,
}: Props) {
  return (
    <div className="flex-shrink-0 w-full h-[calc(100vh-160px)] overflow-x-auto px-[24px]">
      <div className="h-full flex gap-[16px] w-full min-w-max">
        <CollapseCard />
        <div className="flex flex-col h-full overflow-hidden w-full">
          {/* Sticky group (status) headers */}
          <div className="flex gap-[16px] bg-gray-400 flex-shrink-0 sticky top-0 z-20">
            {visibleGroups.map((group) => (
              <div key={group._id} className="w-[255px] flex-shrink-0">
                <GroupHeader group={group} loading={groupLoading} />
              </div>
            ))}
          </div>

          {/* Scrollable subgroup rows */}
          <div className="flex flex-col overflow-y-auto flex-1 overflow-x-hidden">
            {subGroups.map((sub) => {
              const subGroupIdStr = getSubgroupKey(sub._id);
              const subgroupCollapsed = isSubgroupCollapsed[subGroupIdStr];

              return (
                <div key={subGroupIdStr} className="flex flex-col">
                  {/* Subgroup header row */}
                  <div className="sticky top-0 z-10 bg-gray-400 py-2 flex items-center gap-2">
                    <SubGroupHeader
                      group={sub}
                      loading={groupLoading}
                      subgroupCollapsed={subgroupCollapsed}
                      groupIndex={0}
                    />
                    <button className="flex items-center justify-center px-[6px] min-h-5 rounded-full text-xs border border-gray-100 text-secondary hover:bg-gray-400">
                      {subGroupStatsMap[subGroupIdStr]?.count || 0}
                    </button>
                  </div>

                  {/* Subgroup cells — one per status column */}
                  {!subgroupCollapsed && (
                    <div className="flex gap-[16px] overflow-y-auto max-h-[calc(100vh-320px)]">
                      {visibleGroups.map((group) => {
                        const matchedSubs =
                          group.subGroups?.filter(
                            (s) =>
                              getSubgroupKey(s._id) === getSubgroupKey(sub._id),
                          ) || [];

                        const matchedSub: TicketGroup = {
                          ...sub,
                          ticketsList: matchedSubs.flatMap(
                            (s) => s.ticketsList || [],
                          ),
                        };
                        const subGroupIdStr = getSubgroupKey(sub._id);
                        return (
                          <SubgroupCell
                            key={group._id}
                            group={group}
                            matchedSub={matchedSub as TicketGroup}
                            subGroupId={subGroupIdStr}
                          />
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
        <HiddenGroups type={BoardViewType.KANBAN} />
      </div>
    </div>
  );
}
